import { DetalheQuestao } from "store/modules/licenciamentourbano/actions/obterDetalheTipoRequerimento";
import {
  EnderecoLoteFiscal,
  ProprietarioLoteFiscal
} from "store/hooks/useVerificarLoteFiscal";
import {
  TIPO_SECAO_ENDERECO,
  TIPO_SECAO_PROPRIETARIO
} from "utils/secaoTemplateMeta";

const norm = (s: string): string => s.trim().toLowerCase();

/**
 * Identificação das seções prefixadas para o pré-preenchimento por lote fiscal.
 *
 * ⚠️ O backend pode não enviar o discriminador `tipoSecao`/`chave`. Por isso
 * identificamos a seção pelo `tipoSecao` QUANDO presente (mecanismo canônico) e
 * caímos para o NOME da seção como fallback, mantendo o front resiliente até o
 * backend reexpor o discriminador.
 */
export const isSecaoEndereco = (
  tipoSecao?: string | null,
  nome?: string
): boolean =>
  tipoSecao === TIPO_SECAO_ENDERECO || norm(nome ?? "").startsWith("endere");

export const isSecaoProprietario = (
  tipoSecao?: string | null,
  nome?: string
): boolean =>
  tipoSecao === TIPO_SECAO_PROPRIETARIO ||
  norm(nome ?? "").startsWith("propriet");

// enunciado normalizado -> chave do EnderecoLoteFiscal (dados do LOTE).
const ENUNCIADO_ENDERECO: Record<string, keyof EnderecoLoteFiscal> = {
  cep: "cep",
  logradouro: "logradouro",
  endereço: "logradouro",
  endereco: "logradouro",
  numero: "numero",
  número: "numero",
  nº: "numero",
  complemento: "complemento",
  bairro: "bairro",
  cidade: "cidade",
  município: "cidade",
  municipio: "cidade",
  uf: "uf",
  quadra: "quadra",
  lote: "numeroLote",
  "nº do lote": "numeroLote",
  "n° do lote": "numeroLote",
  "numero do lote": "numeroLote",
  "número do lote": "numeroLote",
  matricula: "matricula",
  matrícula: "matricula"
};

// enunciado normalizado -> chave do ProprietarioLoteFiscal (dados do CONTRIBUINTE).
const ENUNCIADO_PROPRIETARIO: Record<string, keyof ProprietarioLoteFiscal> = {
  nome: "nome",
  "nome completo": "nome",
  "nome completo / razão social": "nome",
  "nome / razão social": "nome",
  "nome/razão social": "nome",
  "razão social": "nome",
  "cpf / cnpj": "cpfCnpj",
  "cpf/cnpj": "cpfCnpj",
  cpf: "cpfCnpj",
  cnpj: "cpfCnpj",
  cep: "cep",
  logradouro: "logradouro",
  numero: "numero",
  número: "numero",
  complemento: "complemento",
  bairro: "bairro",
  cidade: "cidade",
  município: "cidade",
  municipio: "cidade",
  uf: "uf",
  telefone: "telefone",
  "e-mail": "email",
  email: "email"
};

const naoVazio = (v?: string | null): v is string =>
  typeof v === "string" && v.trim() !== "";

/**
 * Monta o objeto { [idDaQuestao]: valor } de uma seção Endereço a partir dos dados
 * do lote fiscal. Só inclui campos com valor (não zera campos manuais nem sobrescreve
 * com "não informado" — regra da spec). O logradouro do proprietário concatena
 * tipoLogradouro + logradouro no backend do ENDEREÇO; no proprietário vem separado.
 */
export const camposEnderecoLote = (
  questoes: DetalheQuestao[],
  endereco?: EnderecoLoteFiscal | null
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!endereco) return out;
  questoes.forEach((q) => {
    const chave = ENUNCIADO_ENDERECO[norm(q.enunciado)];
    if (!chave) return;
    const valor = endereco[chave];
    if (naoVazio(valor)) out[q.id] = valor;
  });
  return out;
};

/**
 * Monta o objeto { [idDaQuestao]: valor } de uma ocorrência de Proprietário a partir
 * de um contribuinte do lote. O logradouro do contribuinte pode vir com tipoLogradouro
 * separado — concatenamos para casar com a questão "Logradouro".
 */
export const camposProprietarioLote = (
  questoes: DetalheQuestao[],
  proprietario: ProprietarioLoteFiscal
): Record<string, string> => {
  const out: Record<string, string> = {};
  const logradouroCompleto = naoVazio(proprietario.tipoLogradouro)
    ? `${proprietario.tipoLogradouro} ${proprietario.logradouro ?? ""}`.trim()
    : proprietario.logradouro ?? "";

  questoes.forEach((q) => {
    const chave = ENUNCIADO_PROPRIETARIO[norm(q.enunciado)];
    if (!chave) return;
    const valor =
      chave === "logradouro" ? logradouroCompleto : proprietario[chave];
    if (naoVazio(valor)) out[q.id] = valor;
  });
  return out;
};
